import React, { useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { WorkshopBody, WorkshopDetailDTO } from "../../../../models/WorkshopDTO";
import S from "./strings";
import { Opciones, conMoneda, errorText, numOrNull, usePlantel } from "./comun";
import DiasEditor, { DiaDraft, diaValido } from "./dias";
import { Ventana } from "./piezasTaller";
import { cursoCorto } from "../../../../utils/cursoCorto";

interface Props {
	campusId: number;
	opciones: Opciones;
	taller: WorkshopDetailDTO | null;
	onClose: () => void;
	onSaved: (t: WorkshopDetailDTO, texto: string) => void;
}

/**
 * Create a workshop in two steps, Datos (name, courses, price, capacity, notes) then Días (calendar), with Cancelar visible
 * (Módulos Claros, 2026-11-01). Editing shows only Datos: days are edited in the window, cancelling is its own button
 * («Anular» with «Qué Va a Pasar»), and the teacher's hourly rate lives in «Pago a Maestras».
 */
const WorkshopForm: React.FC<Props> = ({ campusId, opciones, taller, onClose, onSaved }) => {
	const plantel = usePlantel();
	const cur = taller?.currency || plantel.currency;
	const http = HttpManager.getInstance();
	const cursosDelTaller = (taller?.courses?.length ? taller.courses : (taller?.courseId ? [{ id: taller.courseId, name: taller.courseName || "" }] : []))
		.map((c) => ({ id: c.id, name: cursoCorto(c.name) || c.name }));
	const [f, setF] = useState({
		name: taller?.name || "",
		courseIds: cursosDelTaller.map((c) => c.id),
		capacity: taller?.capacity != null ? String(taller.capacity) : "",
		// Talleres en Honduras: L 1,200 al público (Esteban, 2026-10-27); a matriculados el sistema les quita L 200.
		price: taller ? String(taller.price ?? "") : [3, 4].includes(campusId) ? "1200" : "",
		notes: taller?.notes || ""
	});
	const [paso, setPaso] = useState<1 | 2>(1);
	const [dias, setDias] = useState<DiaDraft[]>([]);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const set = (k: keyof typeof f, v: any) => setF((x) => ({ ...x, [k]: v }));
	const datosOk = f.name.trim().length > 1 && f.price.trim() !== "" && !isNaN(Number(f.price));
	const diasOk = taller !== null || (dias.length > 0 && dias.every(diaValido));

	const cursos = [...opciones.cursos];
	cursosDelTaller.forEach((c) => { if (!cursos.some((x) => x.id === c.id)) cursos.unshift(c); });
	const alternarCurso = (id: number) => set("courseIds", f.courseIds.includes(id) ? f.courseIds.filter((x) => x !== id) : [...f.courseIds, id]);

	const guardar = () => {
		if (!datosOk) { setError(S.faltaNombre); setPaso(1); return; }
		if (!diasOk) { setError(S.faltanDias); return; }
		const body: WorkshopBody = { name: f.name.trim(), price: Number(f.price), courseIds: f.courseIds, capacity: numOrNull(f.capacity), notes: f.notes.trim() || null };
		if (!taller) body.sessions = dias.map((d) => ({ date: d.date, startTime: d.startTime, endTime: d.endTime || null, classroomId: d.classroomId || null, mentorId: d.mentorId || null }));
		setBusy(true); setError(null);
		const req = taller ? http.workshopsService.update(campusId, { ...body, id: taller.id }) : http.workshopsService.create(campusId, body);
		req.then((r) => onSaved(r.data, taller ? S.guardado : S.creado))
			.catch((e) => { setError(errorText(e)); setBusy(false); });
	};

	const enDias = !taller && paso === 2;
	const pie = <>
		<button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={busy}>{S.cancelar}</button>
		<span style={{ flex: 1 }} />
		{enDias ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setError(null); setPaso(1); }} disabled={busy}>{S.atras}</button> : null}
		{!taller && paso === 1
			? <button type="button" className="btn btn-primary btn-sm" disabled={!datosOk} onClick={() => { setError(null); setPaso(2); }}>{S.siguiente}</button>
			: <button type="button" className="btn btn-primary btn-sm" disabled={busy || !datosOk || !diasOk} onClick={guardar}>{taller ? S.guardar : S.crear}</button>}
	</>;

	return (
		<Ventana titulo={taller ? S.editarTaller : S.nuevo} onClose={onClose} busy={busy} size={enDias ? "xl" : "lg"} pie={pie}>
			{taller ? null : (
				<div className="wtlPasos" aria-hidden>
					<span className={"wtlPaso" + (paso === 1 ? " on" : "")}><span className="n">1</span>{S.pasoDatos}</span>
					<span className={"wtlPaso" + (paso === 2 ? " on" : "")}><span className="n">2</span>{S.pasoDias}</span>
				</div>
			)}
			{enDias ? <DiasEditor value={dias} onChange={setDias} opciones={opciones} /> : <>
				<div className="wau-fila" style={{ marginBottom: 10 }}>
					<label className="wau-campo" style={{ flex: "2 1 260px" }}><span className="wau-lab">{S.nombre}</span>
						<input className="wau-entrada" value={f.name} onChange={(e) => set("name", e.target.value)} autoFocus /></label>
					<label className="wau-campo" style={{ flex: "1 1 120px" }}><span className="wau-lab">{conMoneda(S.precio, cur)}</span>
						<input type="number" min={0} step="0.01" className="wau-entrada wau-num" value={f.price} onChange={(e) => set("price", e.target.value)} /></label>
					<label className="wau-campo" style={{ flex: "1 1 90px" }}><span className="wau-lab">{S.cupoMax}</span>
						<input type="number" min={1} className="wau-entrada wau-num" value={f.capacity} onChange={(e) => set("capacity", e.target.value)} /></label>
				</div>
				<div className="wau-campo" style={{ marginBottom: 12 }}><span className="wau-lab">{S.cursos}</span>
					<div className="wte-cursos" role="group" aria-label={S.cursos}>
						{cursos.map((c) => <button key={c.id} type="button" className="wau-chip" aria-pressed={f.courseIds.includes(c.id)} onClick={() => alternarCurso(c.id)}>{c.name}</button>)}
					</div>
				</div>
				<label className="wau-campo"><span className="wau-lab">{S.notas}</span>
					<textarea className="wau-entrada" rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></label>
			</>}
			{error ? <div className="waAviso" role="alert">{error}</div> : null}
		</Ventana>
	);
};

export default WorkshopForm;
